
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';

export const useAuthors = () => {
  const { toast } = useToast();

  return useQuery({
    queryKey: ['authors'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('role', 'author')
        .order('name', { ascending: true });
      
      if (error) {
        toast({
          title: 'Error loading authors',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
      
      return data;
    }
  });
};

export const useAuthor = (authorId: string) => {
  const { toast } = useToast();

  return useQuery({
    queryKey: ['author', authorId],
    staleTime: 0, // Always refetch to get latest balance
    queryFn: async () => {
      // First, let's get the author profile with all available columns
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authorId)
        .single();
      
      if (profileError) {
        console.error('Profile fetch error:', profileError);
        toast({
          title: 'Error loading author',
          description: profileError.message,
          variant: 'destructive',
        });
        throw profileError;
      }

      // Get the books separately to avoid join issues
      const { data: booksData, error: booksError } = await supabase
        .from('books')
        .select(`
          id,
          title,
          isbn,
          publish_date
        `)
        .eq('author_id', authorId);
      
      if (booksError) {
        console.error('Books fetch error:', booksError);
      }
      
      // Calculate author balance
      const { data: royalties, error: royaltyError } = await supabase
        .from('royalty_reports')
        .select('royalty_amount')
        .eq('author_id', authorId)
        .eq('status', 'published');
      
      if (royaltyError) {
        console.error('Error fetching royalties:', royaltyError);
      }
      
      // Get all payment requests (both processed and pending) with gross amounts
      const { data: payments, error: paymentsError } = await supabase
        .from('payment_requests')
        .select('amount, status, gross_amount')
        .eq('author_id', authorId)
        .in('status', ['paid', 'approved', 'pending']);
      
      if (paymentsError) {
        console.error('Error fetching payments:', paymentsError);
      }
      
      const totalRoyalties = royalties ? royalties.reduce((sum, r) => sum + Number(r.royalty_amount), 0) : 0;
      
      // Calculate total payments - deduct from royalties what was actually paid out
      const totalPayments = payments ? payments.reduce((sum, p) => {
        if (p.status === 'paid' || p.status === 'approved' || p.status === 'pending') {
          // If tax is 0% or gross_amount is missing/invalid, use the amount directly
          // Otherwise use gross_amount to properly account for pre-tax deductions
          const taxPercent = profileData.tax_percentage || 0;
          let deductionAmount;
          
          if (taxPercent === 0) {
            // No tax - deduct the exact amount requested
            deductionAmount = p.amount;
          } else if (p.gross_amount && p.gross_amount > 0) {
            // Use stored gross amount
            deductionAmount = p.gross_amount;
          } else {
            // Calculate gross from net
            deductionAmount = p.amount / (1 - taxPercent / 100);
          }
          
          return sum + Number(deductionAmount);
        }
        return sum;
      }, 0) : 0;
      
      const balance = Math.max(0, totalRoyalties - totalPayments);
      
      // Fetch payment methods with proper join
      const { data: paymentMethods, error: paymentMethodsError } = await supabase
        .from('payment_methods')
        .select(`
          *,
          payment_details:payment_details (*)
        `)
        .eq('author_id', authorId)
        .order('is_default', { ascending: false });
      
      if (paymentMethodsError) {
        console.error('Error fetching payment methods:', paymentMethodsError);
      }
      
      // Get unique book titles (count by title, not format)
      const uniqueBooks = booksData ? booksData.reduce((acc: any[], book: any) => {
        const existingBook = acc.find(b => b.title === book.title);
        if (!existingBook) {
          acc.push(book);
        }
        return acc;
      }, []) : [];
      
      // Transform data to match our frontend models
      return {
        ...profileData,
        phone: profileData.phone || '',
        address: profileData.address || '',
        books: uniqueBooks.map((book: any) => ({
          ...book,
          publishDate: book.publish_date,
          authorId: authorId
        })),
        balance,
        paymentMethod: paymentMethods && paymentMethods.length > 0 ? {
          ...paymentMethods[0],
          payment_details: paymentMethods[0].payment_details
        } : null,
        paymentDetails: paymentMethods && paymentMethods.length > 0 && paymentMethods[0].payment_details ? 
          paymentMethods[0].payment_details[0] : null
      };
    },
    enabled: !!authorId
  });
};

export const useUpdateAuthor = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...authorData }: { id: string } & Record<string, any>) => {
      const { data, error } = await supabase
        .from('profiles')
        .update(authorData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        toast({
          title: 'Error updating author',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
      
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['author', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['authors'] });
      toast({
        title: 'Success',
        description: 'Author profile has been updated.',
      });
    }
  });
};
